import { useCallback, useEffect, useReducer, useRef } from "react";
import { getFullFileName } from "src/utils/Api";

/**
 * Tracks a single in-flight custom export and coalesces repeated clicks for
 * the same format into the same promise, while forwarding clicks for a
 * different format to `customExportHandler` so its own per-format
 * coalesce/reject logic can run.
 *
 * Why a ref instead of `useState`: two clicks (e.g. CSV then XLSX) can be
 * dispatched inside the same synchronous React batch, before the `disabled`
 * prop from the first click's re-render has committed. `handleCustomExport`
 * must decide "coalesce vs. forward" by reading the *current* in-flight
 * descriptor synchronously, in that same tick — with `useState`, the second
 * call in the batch would still see the pre-update value (still `null`) and
 * would incorrectly treat the different-format click as the first export,
 * silently merging it into the wrong download. The ref is the source of
 * truth for that synchronous decision; `forceRender` only refreshes the
 * derived visual props (`aria-busy`/`disabled`).
 */
export const useCustomExportInFlight = (customExportHandler, fileName) => {
  const inFlightRef = useRef(null);
  const isMountedRef = useRef(true);
  const [, forceRender] = useReducer((tick) => tick + 1, 0);
  const isCustomExporting = Boolean(inFlightRef.current);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const handleCustomExport = useCallback(
    ({ fileType }) => {
      if (!customExportHandler) return undefined;
      // Only coalesce a click for the format already in flight. A click for a
      // different format must reach `customExportHandler` so it can apply its
      // own per-format coalesce/reject logic — otherwise a rapid click on a
      // different-format item (before `disabled` re-renders) would be silently
      // merged into the wrong export and download the wrong file.
      if (inFlightRef.current && inFlightRef.current.fileType === fileType) {
        return inFlightRef.current.promise;
      }

      const promise = Promise.resolve().then(() =>
        customExportHandler({
          fileType,
          fileName: getFullFileName(fileName, true, fileType),
        })
      );

      // Track only the export that actually became active. If another export
      // is already running (different format), leave the ref pointing at it so
      // its in-flight state and eventual cleanup are preserved; this call's
      // rejection/settlement is still handled by the handlers attached below.
      const isFirstInFlight = !inFlightRef.current;
      if (isFirstInFlight) {
        inFlightRef.current = { fileType, promise };
        forceRender();
      }

      const clearInFlight = () => {
        if (inFlightRef.current?.promise !== promise) return;
        inFlightRef.current = null;
        if (isMountedRef.current) forceRender();
      };
      promise.then(clearInFlight, clearInFlight);

      return promise;
    },
    [customExportHandler, fileName]
  );

  return { isCustomExporting, handleCustomExport };
};

export default useCustomExportInFlight;
